import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  RefObject,
} from 'react';
import isNil from 'lodash/isNil';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import TimePicker from 'core/components/DateTimePicker/TimePicker';
import DurationPicker from 'core/components/DateTimePicker/DurationPicker';
import {
  formatDate,
} from 'utils/date';
import {
  DateTimeIntervalValue,
  MIN_APPOINTMENT_DATE,
  MIN_APPOINTMENT_DATE_ERROR_MESSAGE,
  fromStartEnd,
  toEndTime,
} from 'core/components/DateTimePicker/dateTimeInterval';

type Props = {
  value: DateTimeIntervalValue;
  onChange: (value: DateTimeIntervalValue) => void;
  dateInputRef?: RefObject<HTMLInputElement>;
  durationList?: number[];
  suggestedDurationInMinutes?: number;
  timezone?: string;
  onMinDateViolation?: () => void;
  onInputErrorChange?: (message: string | null) => void;
  variant?: 'durationPicker' | 'timepicker';
};

const validateTimeString = (timeString: string) => /^\d{2}:\d{2} (a|p)m$/i.test(timeString);

const toTimestamp = (date?: Dayjs) => (date?.isValid() ? date.valueOf() : null);

const DATE_TIME_FORMAT = 'M/D/YYYY hh:mm a';

function isSameMinute(a?: Dayjs, b?: Dayjs): boolean {
  return (
    a === b
    || (
      !isNil(a) && !isNil(b)
      && a.isValid() && b.isValid()
      && a.isSame(b, 'minute')
    )
  );
}

function parseDateTimeInTz(date: Dayjs, time: string, tz: string | undefined): Dayjs {
  const combined = `${formatDate(date)} ${time}`;
  return isNil(tz) ? dayjs(combined) : dayjs.tz(combined, DATE_TIME_FORMAT, tz);
}

export default function DateTimeIntervalPicker({
  value,
  onChange,
  dateInputRef,
  durationList,
  suggestedDurationInMinutes,
  timezone,
  onMinDateViolation,
  onInputErrorChange,
  variant = 'durationPicker',
}: Props) {
  const [startDate, setStartDate] = useState<Dayjs | null>(value?.startTime);
  const [startTime, setStartTime] = useState<string>(value?.startTime?.format('hh:mm a') ?? '');
  const [endTime, setEndTime] = useState<string>(
    value?.startTime?.isValid()
      ? toEndTime(value).format('hh:mm a')
      : '',
  );
  const [startDateInputError, setStartDateInputError] = useState<string | null>(null);

  const startDateForPicker = useMemo(
    () => (!isNil(startDate) && startDate.isValid()
      ? dayjs(formatDate(startDate), 'M/D/YYYY')
      : startDate),
    [toTimestamp(startDate ?? undefined)],
  );

  const durationStartTime = useMemo(() => (
    !isNil(startDate) && validateTimeString(startTime)
      ? parseDateTimeInTz(startDate, startTime, timezone)
      : null
  ), [startDate, startTime, timezone]);

  const emitStartTime = useCallback((nextDate: Dayjs | null, nextTime: string) => {
    if (!isNil(nextDate) && validateTimeString(nextTime)) {
      onChange({
        ...value,
        startTime: parseDateTimeInTz(nextDate, nextTime, timezone),
      });
    }
  }, [onChange, timezone, value]);

  const handleStartDateChange = useCallback((nextDate: Dayjs | null) => {
    setStartDate(nextDate);
    emitStartTime(nextDate, startTime);
  }, [emitStartTime, startTime]);

  const handleStartTimeChange = useCallback((nextTime: string) => {
    setStartTime(nextTime);
    emitStartTime(startDate, nextTime);
  }, [emitStartTime, startDate]);

  const handleDurationChange = useCallback((durationInMinutes: number) => {
    onChange({
      ...value,
      durationInMinutes,
    });
  }, [onChange, value]);

  const handleEndTimeChange = useCallback((nextEndTime: string) => {
    setEndTime(nextEndTime);

    if (
      isNil(startDate)
      || !validateTimeString(startTime)
      || !validateTimeString(nextEndTime)
    ) {
      return;
    }

    const parsedStart = parseDateTimeInTz(startDate, startTime, timezone);
    const parsedEnd = parseDateTimeInTz(startDate, nextEndTime, timezone);

    onChange(fromStartEnd(parsedStart, parsedEnd));
  }, [onChange, startDate, startTime, timezone]);

  useEffect(() => {
    onInputErrorChange?.(startDateInputError);
  }, [onInputErrorChange, startDateInputError]);

  const stableStartTime = useMemo(
    () => value?.startTime,
    [toTimestamp(value?.startTime)],
  );

  const stableEndTime = useMemo(
    () => (value?.startTime?.isValid() ? toEndTime(value) : null),
    [toTimestamp(value?.startTime), value?.durationInMinutes],
  );

  useEffect(() => {
    setStartDate((previousDate) => (
      isSameMinute(previousDate ?? undefined, stableStartTime)
        ? previousDate
        : stableStartTime
    ));
    setStartTime((previousTime) => {
      const nextTime = stableStartTime?.format('hh:mm a') ?? '';
      return previousTime === nextTime ? previousTime : nextTime;
    });
    setEndTime((previousTime) => {
      const nextTime = stableEndTime?.format('hh:mm a') ?? '';
      return previousTime === nextTime ? previousTime : nextTime;
    });
  }, [stableStartTime, stableEndTime]);

  return (
    <div className="grid grid-cols-3 items-center w-full gap-form-field">
      <div className="min-w-0">
        <DatePicker
          value={startDateForPicker}
          onChange={handleStartDateChange}
          inputRef={dateInputRef}
          className="w-full nw-date-selector populate-date-range"
          inputContainerClassName="w-full"
          containerClassName="w-full space-x-0 !justify-start"
          showDateButtons={false}
          minDate={MIN_APPOINTMENT_DATE}
          enforceMinMaxOnCommit
          invalidValueMessage={MIN_APPOINTMENT_DATE_ERROR_MESSAGE}
          onOutOfRangeDate={onMinDateViolation}
          onInputErrorChange={setStartDateInputError}
        />
      </div>
      <div className="min-w-0">
        <TimePicker
          onChange={handleStartTimeChange}
          value={startTime}
          className="w-full"
        />
      </div>
      <div className="min-w-0">
        {variant === 'durationPicker' ? (
          <DurationPicker
            startTime={durationStartTime}
            value={value.durationInMinutes}
            durationList={durationList}
            suggestedDurationInMinutes={suggestedDurationInMinutes}
            onChange={handleDurationChange}
            className="w-full"
          />
        ) : (
          <TimePicker
            onChange={handleEndTimeChange}
            value={endTime}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
}
